<template>
  <div class="pg-home">
    <AppNavigation />

    <!-- ==================== DEMO REQUEST DIALOG ==================== -->
    <v-dialog v-model="demoFormVisible" max-width="560px" persistent dark content-class="pg-demo-dialog">
      <div class="demo-card">
        <div class="demo-card__head">
          <div>
            <span class="demo-card__eyebrow">Request a demo</span>
            <h2>See PetGroomoz with your salon in mind</h2>
          </div>
          <button class="demo-card__close" aria-label="Close" @click="closeDemoForm">
            <v-icon size="20" color="#C9D6D5">mdi-close</v-icon>
          </button>
        </div>

        <p class="demo-card__sub">
          Tell us a little about your business and we'll reach out to schedule a walkthrough.
        </p>

        <v-form ref="demoForm" v-model="formValid" class="demo-form">
          <v-text-field v-model="demoFormData.name" label="Full name" outlined dense dark color="#F4A259"
            :rules="[rules.required]" prepend-inner-icon="mdi-account-outline" />
          <v-text-field v-model="demoFormData.email" label="Email address" outlined dense dark color="#F4A259"
            :rules="[rules.required, rules.email]" prepend-inner-icon="mdi-email-outline" />
          <v-text-field v-model="demoFormData.phone" label="Phone number (optional)" outlined dense dark color="#F4A259"
            prepend-inner-icon="mdi-phone-outline" />
          <v-text-field v-model="demoFormData.businessName" label="Grooming business name" outlined dense dark color="#F4A259"
            :rules="[rules.required]" prepend-inner-icon="mdi-storefront-outline" />
          <v-select v-model="demoFormData.businessSize" :items="businessSizeOptions" label="Number of groomers"
            outlined dense dark color="#F4A259" item-color="orange" :rules="[rules.required]"
            prepend-inner-icon="mdi-account-group-outline" />
          <v-textarea v-model="demoFormData.message" label="Anything we should know? (optional)" outlined dense dark
            color="#F4A259" rows="3" prepend-inner-icon="mdi-message-text-outline" />
        </v-form>

        <p v-if="formError" class="demo-msg demo-msg--error" role="alert">
          <v-icon size="16" color="#FF9A7A">mdi-alert-circle-outline</v-icon> {{ formError }}
        </p>
        <p v-if="formSuccess" class="demo-msg demo-msg--ok" role="status">
          <v-icon size="16" color="#6BD6C4">mdi-check-circle-outline</v-icon>
          Thank you! We'll be in touch soon to schedule your demo.
        </p>

        <div class="demo-card__actions">
          <v-btn text class="btn-quiet" :disabled="formSubmitting" @click="closeDemoForm">Cancel</v-btn>
          <v-btn depressed class="btn-apricot" :loading="formSubmitting"
            :disabled="!formValid || formSubmitting || formSuccess" @click="submitDemoRequest">
            Send request
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <!-- ==================== HERO ==================== -->
    <section class="hero-section">
      <div class="hero-background">
        <div class="gradient-orb orb-1"></div>
        <div class="gradient-orb orb-2"></div>
      </div>

      <v-container class="hero-content">
        <v-row align="center" justify="center">
          <v-col cols="12" md="6" class="text-center text-md-left pr-md-8">
            <div class="hero-eyebrow mb-6" :class="{ 'is-in': mounted }">
              <v-icon size="15" color="#F7C28F">mdi-paw</v-icon>
              <span>Salons &middot; Mobile groomers &middot; Pet spas</span>
            </div>

            <h1 class="hero-title mb-6" :class="{ 'is-in': mounted }">
              Your grooming business, <em>simplified.</em>
            </h1>

            <p class="hero-subtitle mb-8" :class="{ 'is-in': mounted }">
              The all-in-one platform to manage appointments, delight pet parents, and grow
              your grooming salon — with custom-branded mobile and web apps.
            </p>

            <div class="cta-buttons" :class="{ 'is-in': mounted }">
              <v-btn x-large depressed class="btn-apricot cta-btn mr-sm-3 mb-3" @click="openDemoForm">
                Request a demo
                <v-icon right size="20" color="#2A1608">mdi-arrow-right</v-icon>
              </v-btn>
              <v-btn x-large outlined class="btn-outline cta-btn mb-3" to="/features">
                Explore features
              </v-btn>
            </div>

            <div class="trust-indicators" :class="{ 'is-in': mounted }">
              <div class="trust-badge"><v-icon size="15" color="#6BD6C4">mdi-cash-multiple</v-icon><span>$120/month, flat</span></div>
              <div class="trust-badge"><v-icon size="15" color="#6BD6C4">mdi-medal-outline</v-icon><span>Setup done for you</span></div>
              <div class="trust-badge"><v-icon size="15" color="#6BD6C4">mdi-cellphone-check</v-icon><span>Your branding</span></div>
            </div>
          </v-col>

          <v-col cols="12" md="6" class="text-center">
            <div class="phone-wrap" :class="{ 'is-in': mounted }">
              <div class="phone-glow"></div>
              <div class="phone-frame">
                <div class="phone-screen">
                  <div class="notch"></div>
                  <div class="status-bar">
                    <span>9:41</span>
                    <span class="status-icons">
                      <v-icon size="12" color="#F7F0E6">mdi-signal</v-icon>
                      <v-icon size="12" color="#F7F0E6">mdi-wifi</v-icon>
                      <v-icon size="12" color="#F7F0E6">mdi-battery</v-icon>
                    </span>
                  </div>

                  <div class="app-bar">
                    <span class="app-bar__badge"><img src="@/assets/petgroomoz.png" alt="" /></span>
                    <span class="app-bar__title">Fluffy Paws Grooming</span>
                    <span class="app-bar__bell">
                      <v-icon size="18" color="#C9D6D5">mdi-bell-outline</v-icon>
                      <i></i>
                    </span>
                  </div>

                  <div class="screen-body">
                    <div class="m-welcome">
                      <span class="m-welcome__date">Wednesday, October 7</span>
                      <strong>Today's appointments</strong>
                      <div class="m-stats">
                        <div><b>6</b><span>Today</span></div>
                        <div><b>23</b><span>Upcoming</span></div>
                        <div class="is-accent"><b>3</b><span>Due soon</span></div>
                      </div>
                    </div>

                    <div v-for="a in mockAppointments" :key="a.pet" class="m-appt">
                      <i class="m-appt__bar" :style="{ background: a.bar }"></i>
                      <span class="m-appt__avatar"><v-icon size="18" color="#F4A259">{{ a.icon }}</v-icon></span>
                      <div class="m-appt__info">
                        <strong>{{ a.pet }} <em>{{ a.service }}</em></strong>
                        <span>{{ a.time }} · {{ a.owner }}</span>
                      </div>
                      <span class="m-appt__bell"><v-icon size="14" color="#F4A259">mdi-bell-ring-outline</v-icon></span>
                    </div>

                    <div class="m-loyalty">
                      <div class="m-loyalty__top">
                        <span><v-icon size="14" color="#F4A259">mdi-star-circle-outline</v-icon> Max · loyalty</span>
                        <b>8/10 visits</b>
                      </div>
                      <div class="m-loyalty__track"><i></i></div>
                    </div>
                  </div>

                  <div class="m-nav">
                    <v-icon size="20" color="#F4A259">mdi-home-variant</v-icon>
                    <v-icon size="20" color="#6F8A8C">mdi-calendar-month-outline</v-icon>
                    <v-icon size="20" color="#6F8A8C">mdi-paw</v-icon>
                    <v-icon size="20" color="#6F8A8C">mdi-message-text-outline</v-icon>
                    <v-icon size="20" color="#6F8A8C">mdi-cog-outline</v-icon>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ==================== FEATURES ==================== -->
    <section class="features-section">
      <v-container>
        <div class="section-header text-center reveal">
          <div class="section-badge mb-4"><v-icon size="14" color="#F4A259">mdi-star-four-points</v-icon><span>ONE PLATFORM</span></div>
          <h2 class="section-title mb-4">Everything your grooming business needs</h2>
          <p class="section-subtitle">
            From the first booking to the paid invoice — the whole day runs from one dashboard.
          </p>
        </div>

        <div class="feature-grid">
          <article
            v-for="(feature, index) in features"
            :key="feature.title"
            class="feature-card reveal"
            :style="{ transitionDelay: `${(index % 3) * 80}ms` }"
          >
            <span class="feature-icon" :style="iconStyle(feature.color)">
              <v-icon size="24" :color="feature.color">{{ feature.icon }}</v-icon>
            </span>
            <h3 class="feature-title">{{ feature.title }}</h3>
            <p class="feature-description">{{ feature.description }}</p>
            <ul class="feature-benefits">
              <li v-for="b in feature.benefits" :key="b">
                <v-icon size="15" color="#6BD6C4">mdi-check</v-icon><span>{{ b }}</span>
              </li>
            </ul>
          </article>
        </div>

        <div class="text-center mt-10 reveal">
          <v-btn large outlined class="btn-outline" to="/features">
            See every feature
            <v-icon right size="18">mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </v-container>
    </section>

    <!-- ==================== INSIDE THE DASHBOARD ==================== -->
    <section class="demo-section">
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="6" order="2" order-md="1">
            <div class="demo-interface reveal">
              <div class="demo-tabs" role="tablist" aria-label="Dashboard areas">
                <button
                  v-for="(tab, index) in demoTabs"
                  :key="tab.label"
                  role="tab"
                  class="demo-tab"
                  :class="{ active: activeTab === index }"
                  :aria-selected="activeTab === index"
                  @click="activeTab = index"
                >
                  <v-icon size="18">{{ tab.icon }}</v-icon>
                  <span>{{ tab.label }}</span>
                </button>
              </div>

              <transition name="fade" mode="out-in">
                <div :key="activeTab" class="demo-panel">
                  <div class="demo-visual" :style="{ '--tone': demoTabs[activeTab].color }">
                    <v-icon size="56" :color="demoTabs[activeTab].color">{{ demoTabs[activeTab].icon }}</v-icon>
                  </div>
                  <h3 class="demo-panel-title">{{ demoTabs[activeTab].title }}</h3>
                  <p class="demo-panel-text">{{ demoTabs[activeTab].description }}</p>
                  <ol class="demo-list">
                    <li v-for="(item, idx) in demoTabs[activeTab].items" :key="item">
                      <span class="demo-list__num">{{ idx + 1 }}</span>
                      <span>{{ item }}</span>
                    </li>
                  </ol>
                </div>
              </transition>
            </div>
          </v-col>

          <v-col cols="12" md="6" order="1" order-md="2" class="pl-md-10 mb-10 mb-md-0">
            <div class="reveal">
              <div class="section-badge mb-4"><v-icon size="14" color="#F4A259">mdi-monitor-dashboard</v-icon><span>INSIDE THE DASHBOARD</span></div>
              <h2 class="section-title mb-4">Designed around a real day at the salon</h2>
              <p class="section-text mb-8">
                Every screen answers a question you actually ask: who's on the table today,
                which pets are overdue, who still owes, and how the month is going.
              </p>
              <div class="info-stats">
                <div v-for="stat in platformStats" :key="stat.label" class="stat-item">
                  <span class="stat-icon" :style="iconStyle(stat.color)">
                    <v-icon size="20" :color="stat.color">{{ stat.icon }}</v-icon>
                  </span>
                  <div>
                    <div class="stat-value">{{ stat.value }}</div>
                    <div class="stat-label">{{ stat.label }}</div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ==================== WHO IT'S FOR ==================== -->
    <section class="benefits-section">
      <v-container>
        <div class="section-header text-center reveal">
          <div class="section-badge mb-4"><v-icon size="14" color="#F4A259">mdi-heart-outline</v-icon><span>BUILT FOR GROOMERS</span></div>
          <h2 class="section-title mb-4">One app, three points of view</h2>
          <p class="section-subtitle">Owners, groomers, and pet parents each get what they need — nothing they don't.</p>
        </div>

        <v-row>
          <v-col v-for="(benefit, index) in coreBenefits" :key="benefit.title" cols="12" md="4" class="mb-6">
            <div class="benefit-card reveal" :style="{ transitionDelay: `${index * 100}ms` }">
              <span class="benefit-icon" :style="iconStyle(benefit.color)">
                <v-icon size="26" :color="benefit.color">{{ benefit.icon }}</v-icon>
              </span>
              <div class="benefit-badge">{{ benefit.badge }}</div>
              <h3 class="benefit-title">{{ benefit.title }}</h3>
              <p class="benefit-text">{{ benefit.description }}</p>
              <ul class="benefit-points">
                <li v-for="p in benefit.points" :key="p">{{ p }}</li>
              </ul>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ==================== TRIBZZ ==================== -->
    <section class="tribzz-section">
      <v-container>
        <div class="tribzz-band reveal">
          <div class="tribzz-copy">
            <div class="section-badge mb-4"><v-icon size="14" color="#F4A259">mdi-account-group-outline</v-icon><span>NEW · TRIBZZ</span></div>
            <h2 class="section-title tribzz-title mb-4">Give your regulars a place to gather</h2>
            <p class="tribzz-text">
              Tribzz is the social platform from the team behind PetGroomoz. Start a free Tribzz for your salon to post
              fresh grooms, fill last-minute openings and share deals with the clients who already love you. No ads, ever.
            </p>
            <ul class="tribzz-points">
              <li><v-icon size="18" color="#6BD6C4">mdi-check-bold</v-icon><span>Before-and-afters your clients actually see</span></li>
              <li><v-icon size="18" color="#6BD6C4">mdi-check-bold</v-icon><span>Post an opening and let a regular grab it</span></li>
              <li><v-icon size="18" color="#6BD6C4">mdi-check-bold</v-icon><span>Deals, polls and events for your followers</span></li>
            </ul>
            <div class="tribzz-actions">
              <v-btn large depressed class="btn-apricot" to="/tribzz">See how groomers use it</v-btn>
              <a :href="tribzzUrl" target="_blank" rel="noopener" class="tribzz-link">Go to Tribzz <v-icon size="16" color="#F7C28F">mdi-open-in-new</v-icon></a>
            </div>
          </div>
          <div class="tribzz-mock" aria-hidden="true">
            <div class="tm-card">
              <div class="tm-cover"><span>H</span></div>
              <div class="tm-body">
                <div class="tm-title">Happy Paws Grooming</div>
                <div class="tm-sub">Fresh cuts, open chairs and bath-day deals.</div>
                <div class="tm-chips"><span>Appointments</span><span>Photos</span><span>Deals</span></div>
                <div class="tm-post">&ldquo;2 spots open this afternoon ✂&rdquo;</div>
                <div class="tm-join">Join</div>
              </div>
            </div>
          </div>
        </div>
      </v-container>
    </section>

    <!-- ==================== PRICING ==================== -->
    <section id="pricing" class="pricing-section">
      <v-container>
        <div class="section-header text-center reveal">
          <div class="section-badge mb-4"><v-icon size="14" color="#F4A259">mdi-currency-usd</v-icon><span>SIMPLE PRICING</span></div>
          <h2 class="section-title mb-4">One price. Everything included.</h2>
          <p class="section-subtitle">No tiers to choose, no add-on modules, no surprises.</p>
        </div>

        <div class="pricing-card reveal">
          <div class="pricing-summary">
            <span class="pricing-logo"><img src="@/assets/petgroomoz.png" alt="" /></span>
            <h3 class="pricing-plan-name">PetGroomoz Complete</h3>
            <p class="pricing-tagline">Everything you need to run the salon</p>

            <div class="pricing-amount">
              <span class="currency">$</span><span class="amount">120</span><span class="period">/month</span>
            </div>
            <p class="pricing-note">+ $499 one-time onboarding · No contracts · Cancel anytime</p>

            <v-btn x-large block depressed class="btn-apricot mb-5" @click="openDemoForm">
              Request a demo
            </v-btn>

            <div class="pricing-guarantees">
              <div><v-icon size="16" color="#6BD6C4">mdi-check-decagram-outline</v-icon><span>We do the setup &amp; training</span></div>
              <div><v-icon size="16" color="#6BD6C4">mdi-check-decagram-outline</v-icon><span>Apps under your salon's name</span></div>
              <div><v-icon size="16" color="#6BD6C4">mdi-check-decagram-outline</v-icon><span>Updates included</span></div>
            </div>
          </div>

          <div class="pricing-features">
            <div v-for="group in featureGroups" :key="group.title" class="feature-group">
              <div class="group-header">
                <v-icon size="17" color="#F4A259">{{ group.icon }}</v-icon>
                <h4>{{ group.title }}</h4>
              </div>
              <ul>
                <li v-for="f in group.features" :key="f">
                  <v-icon size="15" color="#6BD6C4">mdi-check</v-icon><span>{{ f }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </v-container>
    </section>

    <!-- ==================== FINAL CTA ==================== -->
    <section class="final-cta-section">
      <div class="cta-orb"></div>
      <v-container class="text-center cta-inner reveal">
        <v-icon size="44" color="#F4A259" class="mb-5">mdi-paw</v-icon>
        <h2 class="cta-title mb-4">Ready to run your salon without the chaos?</h2>
        <p class="cta-subtitle mb-8">
          Fifteen minutes is usually enough to know whether PetGroomoz fits your business.
        </p>
        <v-btn x-large depressed class="btn-apricot cta-btn mr-sm-3 mb-3" @click="openDemoForm">
          Request a demo
          <v-icon right size="20" color="#2A1608">mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn x-large outlined class="btn-outline cta-btn mb-3" to="/why">Why PetGroomoz</v-btn>
      </v-container>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import { db } from '@/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import AppNavigation from '@/components/AppNavigation.vue';
import AppFooter from '@/components/AppFooter.vue';
import { TRIBZZ_URL } from '@/utils/tribzz';

const emptyDemoForm = () => ({ name: '', email: '', phone: '', businessName: '', businessSize: '', message: '' });

export default {
  name: 'PetGroomozHome',
  components: { AppNavigation, AppFooter },

  data: () => ({
    tribzzUrl: TRIBZZ_URL,
    mounted: false,
    activeTab: 0,

    // Demo form
    demoFormVisible: false,
    formValid: false,
    formSubmitting: false,
    formError: '',
    formSuccess: false,
    demoFormData: emptyDemoForm(),
    businessSizeOptions: ['1-2 groomers', '3-5 groomers', '6-10 groomers', '11+ groomers'],
    rules: {
      required: (value) => !!value || 'This field is required',
      email: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'Invalid email address'
    },

    mockAppointments: [
      { pet: 'Max', service: 'Full groom', time: '10:00 AM', owner: 'Sarah Johnson', icon: 'mdi-dog', bar: 'linear-gradient(180deg,#F7B267,#E8734A)' },
      { pet: 'Whiskers', service: 'Bath & trim', time: '2:30 PM', owner: 'Mike Chen', icon: 'mdi-cat', bar: '#6BD6C4' },
      { pet: 'Bella', service: 'Nail trim', time: '4:00 PM', owner: 'Ana Ruiz', icon: 'mdi-dog', bar: '#2B4A4E' }
    ],

    features: [
      {
        icon: 'mdi-calendar-month-outline', color: '#FFD27A',
        title: 'Calendar & booking',
        description: 'A schedule for every groomer, with working hours, closed days, and bookings in one place.',
        benefits: ['Per-groomer schedules', 'Book a client and pet in one step', 'Today and upcoming at a glance']
      },
      {
        icon: 'mdi-paw', color: '#F4A259',
        title: 'Pet profiles',
        description: 'Breed, size, coat, and handling notes on file, so every groomer knows the pet before it arrives.',
        benefits: ['Groom-due status for every pet', 'Handling notes flagged up front', 'One tap to mark "groomed today"']
      },
      {
        icon: 'mdi-needle', color: '#7FD89A',
        title: 'Vaccination tracking',
        description: 'Records and expiration dates per pet, with anything expired or missing surfaced automatically.',
        benefits: ['Expired, expiring, and missing flags', 'Staff-verified records', 'Warnings on the pet and the invoice']
      },
      {
        icon: 'mdi-receipt-text-outline', color: '#6FB7FF',
        title: 'Invoices & payments',
        description: 'Bill from your service menu, email a payment link, or log cash and card taken at the counter.',
        benefits: ['Clients can pay online', 'Print or download PDF invoices', 'Outstanding and overdue totals']
      },
      {
        icon: 'mdi-tag-heart-outline', color: '#C9A2FF',
        title: 'Services & pricing',
        description: 'Your own menu of baths, cuts, and add-ons — priced flat or by pet size.',
        benefits: ['Prices by pet size', 'Add-ons and species-specific services', 'One-tap line items on invoices']
      },
      {
        icon: 'mdi-message-text-outline', color: '#6BD6C4',
        title: 'Messaging & reminders',
        description: 'Chat with owners in the app and send appointment or time-to-rebook reminders as push notifications.',
        benefits: ['Direct chat with pet parents', 'Appointment reminders in one tap', '"Time for a groom" nudges']
      },
      {
        icon: 'mdi-star-circle-outline', color: '#FF8A7A',
        title: 'Loyalty & referrals',
        description: 'Every tenth visit earns a free groom, and clients who send friends earn referral points.',
        benefits: ['Visit punch card per pet', 'Referral tracking and rewards', 'Thank-you notifications']
      },
      {
        icon: 'mdi-chart-line', color: '#6BD6C4',
        title: 'Reports',
        description: 'Revenue, bookings, and client trends for the last week, month, quarter, or year.',
        benefits: ['Top services and top clients', 'Busiest days and groomer workload', 'Rebooking rate']
      },
      {
        icon: 'mdi-cellphone-link', color: '#FFD27A',
        title: 'Custom-branded apps',
        description: 'Mobile and web apps that carry your salon’s name, logo, and colors — not ours.',
        benefits: ['Your branding throughout', 'Apps for clients and staff', 'Web dashboard for the front desk']
      }
    ],

    demoTabs: [
      {
        icon: 'mdi-calendar-month-outline', label: 'Calendar', color: '#FFD27A',
        title: 'Every groomer’s day, side by side',
        description: 'Pick a groomer, see their day, and book the next client without leaving the page.',
        items: [
          'Set working hours and closed days per groomer',
          'See how many appointments each groomer has today',
          'Book straight from a client or pet profile',
          'Upcoming appointments on the dashboard, most urgent first'
        ]
      },
      {
        icon: 'mdi-paw', label: 'Pets', color: '#F4A259',
        title: 'Know who’s due before they call',
        description: 'Every pet sorted by how overdue they are, with the actions you need right on the row.',
        items: [
          'Filter by due for a groom, vaccine issues, or handling notes',
          'Book, invoice, or message the owner from the pet',
          'Send a "time to book" reminder',
          'Mark a pet groomed today and the next due date updates'
        ]
      },
      {
        icon: 'mdi-receipt-text-outline', label: 'Invoices', color: '#6FB7FF',
        title: 'From groom to paid in a few taps',
        description: 'Build an invoice from your service menu or log a walk-in payment on the spot.',
        items: [
          'Service shortcuts priced for the pet’s size',
          'Email or copy a secure payment link',
          'Mark paid by cash, debit, or credit',
          'Link an invoice to an appointment and a loyalty visit'
        ]
      },
      {
        icon: 'mdi-chart-line', label: 'Reports', color: '#6BD6C4',
        title: 'Numbers you can act on',
        description: 'Each period compared with the one before it, so you can see what’s changing.',
        items: [
          'Revenue, appointments, and average ticket',
          'Top services and top clients by spend',
          'Vaccine compliance and groom schedule health',
          'Average rating and review breakdown'
        ]
      }
    ],

    platformStats: [
      { icon: 'mdi-view-dashboard-outline', value: '12 modules', label: 'One dashboard', color: '#F4A259' },
      { icon: 'mdi-account-multiple-outline', value: '3 roles', label: 'Admin, groomer, client', color: '#6BD6C4' },
      { icon: 'mdi-cellphone-link', value: 'Mobile + web', label: 'Wherever you work', color: '#6FB7FF' },
      { icon: 'mdi-palette-outline', value: 'Your brand', label: 'Name, logo, colors', color: '#C9A2FF' }
    ],

    coreBenefits: [
      {
        icon: 'mdi-storefront-outline', color: '#F4A259', badge: 'FOR SALON OWNERS',
        title: 'See the whole business',
        description: 'Clients, staff, money, and reviews in one place instead of a paper book and three apps.',
        points: ['Revenue and booking reports', 'Team, hours, and service menu', 'Outstanding invoices at a glance', 'Reviews and referrals']
      },
      {
        icon: 'mdi-content-cut', color: '#6BD6C4', badge: 'FOR GROOMERS',
        title: 'Walk in prepared',
        description: 'Your schedule and everything you need to know about the pet on your table.',
        points: ['Your own daily schedule', 'Coat, style, and handling notes', 'Vaccine status before you start', 'Message the owner directly']
      },
      {
        icon: 'mdi-heart-outline', color: '#FF8A7A', badge: 'FOR PET PARENTS',
        title: 'Easy to come back',
        description: 'An app with your salon’s name on it that makes booking and paying effortless.',
        points: ['Book and get reminders', 'Pay invoices online', 'Earn a free groom with visits', 'Refer friends for rewards']
      }
    ],

    featureGroups: [
      { icon: 'mdi-calendar-star', title: 'Appointments', features: ['Per-groomer calendars', 'Working hours & closed days', 'Appointment reminders', 'Upcoming-visit dashboard'] },
      { icon: 'mdi-paw', title: 'Pets & care', features: ['Detailed pet profiles', 'Groom-due tracking', 'Vaccination records', 'Handling notes'] },
      { icon: 'mdi-cash-register', title: 'Billing', features: ['Invoices & payment links', 'Cash and card logging', 'PDF & print', 'Service menu by pet size'] },
      { icon: 'mdi-gift-outline', title: 'Client engagement', features: ['In-app messaging', 'Loyalty free-groom rewards', 'Referral points', 'Client reviews'] },
      { icon: 'mdi-chart-timeline-variant', title: 'Business tools', features: ['Revenue & booking reports', 'Rebooking rate', 'Team management', 'Client directory'] },
      { icon: 'mdi-cellphone-check', title: 'Branding & support', features: ['Custom-branded mobile apps', 'Branded web portal', 'Setup & training done for you', 'Updates included'] }
    ]
  }),

  watch: {
    // AppNavigation and AppFooter open the form from any page via /?demo=1
    '$route.query.demo': {
      immediate: true,
      handler(v) {
        if (v) this.openDemoForm();
      }
    }
  },

  mounted() {
    requestAnimationFrame(() => (this.mounted = true));
    this.setupReveal();
  },

  beforeDestroy() {
    if (this._observer) this._observer.disconnect();
    clearTimeout(this._closeTimer);
  },

  methods: {
    iconStyle(color) {
      return { background: color + '1F', border: `1px solid ${color}55` };
    },

    openDemoForm() {
      this.demoFormVisible = true;
      this.formError = '';
      this.formSuccess = false;
    },

    closeDemoForm() {
      clearTimeout(this._closeTimer);
      this.demoFormVisible = false;
      this.demoFormData = emptyDemoForm();
      this.formError = '';
      this.formSuccess = false;
      if (this.$refs.demoForm) this.$refs.demoForm.resetValidation();
      // Drop ?demo=1 so the nav button can reopen the form
      if (this.$route.query.demo) this.$router.replace({ path: '/' }).catch(() => {});
    },

    async submitDemoRequest() {
      if (!this.$refs.demoForm.validate()) return;

      this.formSubmitting = true;
      this.formError = '';
      this.formSuccess = false;

      try {
        await addDoc(collection(db, 'contact_requests'), {
          name: this.demoFormData.name,
          email: this.demoFormData.email,
          phone: this.demoFormData.phone || '',
          businessName: this.demoFormData.businessName,
          businessSize: this.demoFormData.businessSize,
          message: this.demoFormData.message || '',
          createdAt: serverTimestamp(),
          read: false,
          status: 'new',
          source: 'petgroomoz_website'
        });
        this.formSuccess = true;
        this._closeTimer = setTimeout(() => this.closeDemoForm(), 3000);
      } catch (error) {
        console.error('Error submitting demo request:', error);
        this.formError = 'Your request didn’t send. Check your connection and try again.';
      } finally {
        this.formSubmitting = false;
      }
    },

    // Fade sections in as they scroll into view
    setupReveal() {
      const els = this.$el.querySelectorAll('.reveal');
      if (!('IntersectionObserver' in window)) {
        els.forEach((el) => el.classList.add('is-visible'));
        return;
      }
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              this._observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
      );
      els.forEach((el) => this._observer.observe(el));
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700;800&family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&display=swap');

* { box-sizing: border-box; }

.pg-home {
  --bg-page: #0D2226;
  --bg-alt: #0F272C;
  --bg-card: #122D32;
  --bg-card-hover: #15343A;
  --bg-deep: #081619;
  --border-subtle: rgba(247, 240, 230, 0.08);
  --border-apricot: rgba(244, 162, 89, 0.38);
  --text-primary: #F7F0E6;
  --text-secondary: rgba(247, 240, 230, 0.72);
  --text-muted: rgba(247, 240, 230, 0.5);
  --apricot: #F4A259;
  --apricot-light: #F7C28F;
  --mint: #6BD6C4;

  overflow-x: hidden;
  background: var(--bg-page);
  padding-top: 70px;
  font-family: 'Figtree', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--text-primary);
}

.pg-home h1, .pg-home h2,
.pg-home .feature-title, .pg-home .benefit-title,
.pg-home .demo-panel-title, .pg-home .pricing-plan-name {
  font-family: 'Fraunces', Georgia, serif;
  letter-spacing: -0.01em;
}

.pg-home ::v-deep .v-btn { text-transform: none !important; letter-spacing: 0 !important; }

/* ---------- Buttons ---------- */
.btn-apricot {
  background: linear-gradient(135deg, #F7B267 0%, #EE8A4E 100%) !important;
  color: #2A1608 !important;
  font-weight: 800 !important;
  border-radius: 12px !important;
  box-shadow: 0 10px 24px -10px rgba(238, 138, 78, 0.65) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.btn-apricot:hover { transform: translateY(-1px); box-shadow: 0 14px 28px -10px rgba(238, 138, 78, 0.8) !important; }
.btn-apricot.v-btn--disabled { opacity: 0.5; box-shadow: none !important; }

.btn-outline {
  border: 1.5px solid rgba(247, 240, 230, 0.22) !important;
  color: var(--text-primary) !important;
  font-weight: 700 !important;
  border-radius: 12px !important;
}
.btn-outline:hover { border-color: var(--apricot) !important; color: var(--apricot-light) !important; }
.btn-quiet { color: var(--text-secondary) !important; font-weight: 700 !important; }
.cta-btn { font-size: 16px !important; padding: 0 30px !important; }

/* ---------- Scroll reveal ---------- */
.reveal { opacity: 0; transform: translateY(22px); transition: opacity 0.7s ease, transform 0.7s ease; }
.reveal.is-visible { opacity: 1; transform: none; }

/* ==================== HERO ==================== */
.hero-section {
  position: relative;
  min-height: 92vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 72px 0 96px;
  background: radial-gradient(120% 90% at 0% 0%, #174A50 0%, #0D2226 55%, #081619 100%);
}
.hero-background { position: absolute; inset: 0; pointer-events: none; }
.gradient-orb { position: absolute; border-radius: 50%; filter: blur(100px); }
.orb-1 { width: 560px; height: 560px; top: -200px; right: -160px; opacity: 0.32;
  background: radial-gradient(circle, rgba(244, 162, 89, 0.6) 0%, transparent 70%); }
.orb-2 { width: 460px; height: 460px; bottom: -220px; left: 10%; opacity: 0.2;
  background: radial-gradient(circle, rgba(107, 214, 196, 0.55) 0%, transparent 70%); }
.hero-content { position: relative; z-index: 2; }

.hero-eyebrow, .hero-title, .hero-subtitle, .cta-buttons, .trust-indicators {
  opacity: 0; transform: translateY(16px);
  transition: opacity 0.8s ease, transform 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.hero-eyebrow.is-in, .hero-title.is-in, .hero-subtitle.is-in, .cta-buttons.is-in, .trust-indicators.is-in { opacity: 1; transform: none; }
.hero-title { transition-delay: 0.1s; }
.hero-subtitle { transition-delay: 0.2s; }
.cta-buttons { transition-delay: 0.3s; }
.trust-indicators { transition-delay: 0.4s; }

.hero-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 8px 16px;
  border: 1px solid rgba(247, 240, 230, 0.16);
  border-radius: 999px;
  font-size: 0.78rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  color: rgba(247, 240, 230, 0.85);
}

.hero-title { font-size: clamp(2.5rem, 5.2vw, 4rem); font-weight: 600; line-height: 1.08; color: var(--text-primary); }
.hero-title em { font-style: italic; color: var(--apricot); }

.hero-subtitle { font-size: 1.15rem; line-height: 1.65; color: var(--text-secondary); max-width: 540px; }

.trust-indicators { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 24px; }
.trust-badge {
  display: flex; align-items: center; gap: 7px;
  padding: 7px 14px;
  background: rgba(247, 240, 230, 0.05);
  border: 1px solid var(--border-subtle);
  border-radius: 999px;
  font-size: 13px; font-weight: 600; color: var(--text-secondary);
}

/* ---------- Phone mockup ---------- */
.phone-wrap { position: relative; display: inline-block; opacity: 0; transform: translateY(40px); transition: opacity 1s ease 0.3s, transform 1s ease 0.3s; }
.phone-wrap.is-in { opacity: 1; transform: none; animation: gentle-float 7s 1.4s infinite ease-in-out; }
@keyframes gentle-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-14px); } }

.phone-glow { position: absolute; inset: -12%; background: radial-gradient(circle, rgba(244, 162, 89, 0.22) 0%, transparent 65%); filter: blur(40px); }

.phone-frame {
  position: relative;
  width: 320px; height: 650px;
  background: #050D0F;
  border-radius: 44px;
  padding: 11px;
  box-shadow: 0 0 0 1.5px rgba(247, 240, 230, 0.14), 0 40px 90px rgba(0, 0, 0, 0.55);
  text-align: left;
}
.phone-screen { position: relative; width: 100%; height: 100%; background: #0D2226; border-radius: 34px; overflow: hidden; display: flex; flex-direction: column; }
.notch { position: absolute; top: 0; left: 50%; transform: translateX(-50%); width: 120px; height: 22px; background: #050D0F; border-radius: 0 0 16px 16px; z-index: 2; }
.status-bar { display: flex; justify-content: space-between; align-items: center; padding: 8px 20px 4px; font-size: 12px; font-weight: 700; color: #F7F0E6; }
.status-icons { display: flex; gap: 4px; }

.app-bar { display: flex; align-items: center; gap: 10px; padding: 10px 16px; border-bottom: 1px solid var(--border-subtle); }
.app-bar__badge { width: 28px; height: 28px; border-radius: 50%; background: #F7F0E6; overflow: hidden; flex: 0 0 auto; }
.app-bar__badge img { width: 100%; height: 100%; object-fit: cover; }
.app-bar__title { flex: 1; font-size: 14px; font-weight: 800; color: #F7F0E6; }
.app-bar__bell { position: relative; display: flex; }
.app-bar__bell i { position: absolute; top: 0; right: 0; width: 7px; height: 7px; border-radius: 50%; background: var(--apricot); }

.screen-body { flex: 1; padding: 14px; display: flex; flex-direction: column; gap: 9px; overflow: hidden; }

.m-welcome { padding: 14px; border-radius: 14px; background: radial-gradient(120% 160% at 0% 0%, #174A50 0%, #11292E 60%); border: 1px solid var(--border-subtle); }
.m-welcome__date { display: block; color: var(--apricot); font-size: 10.5px; font-weight: 700; margin-bottom: 3px; }
.m-welcome strong { font-size: 16px; font-weight: 800; color: #F7F0E6; }
.m-stats { display: flex; gap: 7px; margin-top: 11px; }
.m-stats div { flex: 1; display: flex; flex-direction: column; align-items: center; padding: 7px 4px; border-radius: 10px; background: rgba(13, 34, 38, 0.6); border: 1px solid var(--border-subtle); }
.m-stats b { font-size: 16px; font-weight: 800; color: #F7F0E6; }
.m-stats span { font-size: 9.5px; font-weight: 600; color: #86A3A3; }
.m-stats .is-accent { background: rgba(244, 162, 89, 0.12); border-color: rgba(244, 162, 89, 0.35); }
.m-stats .is-accent b { color: var(--apricot); }

.m-appt { display: flex; align-items: center; gap: 9px; padding: 9px 10px 9px 8px; border-radius: 12px; background: #122D32; border: 1px solid var(--border-subtle); }
.m-appt__bar { width: 3px; align-self: stretch; border-radius: 3px; }
.m-appt__avatar { width: 34px; height: 34px; flex: 0 0 auto; border-radius: 50%; background: rgba(244, 162, 89, 0.12); border: 1px solid rgba(244, 162, 89, 0.3); display: flex; align-items: center; justify-content: center; }
.m-appt__info { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.m-appt__info strong, .m-appt__info span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.m-appt__info strong { font-size: 12.5px; font-weight: 700; color: #F7F0E6; }
.m-appt__info em { font-style: normal; font-weight: 600; color: var(--apricot-light); }
.m-appt__info span { font-size: 10.5px; color: #86A3A3; }
.m-appt__bell { width: 28px; height: 28px; border-radius: 8px; border: 1px solid var(--border-subtle); background: #15343A; display: flex; align-items: center; justify-content: center; }

.m-loyalty { padding: 11px 12px; border-radius: 12px; background: rgba(244, 162, 89, 0.08); border: 1px solid rgba(244, 162, 89, 0.3); }
.m-loyalty__top { display: flex; justify-content: space-between; align-items: center; font-size: 11.5px; font-weight: 700; color: #F1ECE4; margin-bottom: 8px; }
.m-loyalty__top span { display: flex; align-items: center; gap: 5px; }
.m-loyalty__top b { color: var(--apricot); }
.m-loyalty__track { height: 6px; border-radius: 3px; background: #0D2226; overflow: hidden; }
.m-loyalty__track i { display: block; width: 80%; height: 100%; border-radius: 3px; background: linear-gradient(90deg, #EE8A4E, #F7B267); }

.m-nav { display: flex; justify-content: space-around; align-items: center; padding: 12px 16px 16px; background: #0B1D21; border-top: 1px solid var(--border-subtle); }

/* ==================== SECTIONS (shared) ==================== */
.features-section, .benefits-section, .pricing-section { padding: 104px 0; background: var(--bg-page); }
.demo-section { padding: 104px 0; background: var(--bg-alt); border-top: 1px solid var(--border-subtle); border-bottom: 1px solid var(--border-subtle); }
.section-header { margin-bottom: 56px; }

.section-badge {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 7px 16px;
  background: rgba(244, 162, 89, 0.1);
  border: 1px solid var(--border-apricot);
  border-radius: 999px;
  font-size: 12px; font-weight: 800; letter-spacing: 1.2px;
  color: var(--apricot-light);
}
.section-title { font-size: clamp(2rem, 4vw, 2.9rem); font-weight: 600; line-height: 1.15; color: var(--text-primary); }
.section-subtitle { font-size: 1.1rem; line-height: 1.6; color: var(--text-secondary); max-width: 640px; margin: 0 auto; }
.section-text { font-size: 1.05rem; line-height: 1.75; color: var(--text-secondary); }

/* ==================== FEATURES ==================== */
.feature-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }

.feature-card {
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
  padding: 30px 28px;
  transition: opacity 0.7s ease, transform 0.7s ease, border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
}
.feature-card.is-visible:hover { transform: translateY(-4px); background: var(--bg-card-hover); border-color: var(--border-apricot); box-shadow: 0 24px 50px -20px rgba(0, 0, 0, 0.55); }

.feature-icon, .benefit-icon { width: 52px; height: 52px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 20px; }
.feature-title { font-size: 1.3rem; font-weight: 600; color: var(--text-primary); margin-bottom: 10px; }
.feature-description { font-size: 0.96rem; line-height: 1.65; color: var(--text-secondary); margin-bottom: 18px; }

.feature-benefits { list-style: none; padding: 16px 0 0; margin: 0; border-top: 1px solid var(--border-subtle); display: flex; flex-direction: column; gap: 9px; }
.feature-benefits li { display: flex; align-items: flex-start; gap: 9px; font-size: 0.9rem; line-height: 1.45; color: var(--text-secondary); }
.feature-benefits li .v-icon { margin-top: 2px; }

/* ==================== DASHBOARD TABS ==================== */
.demo-interface { background: var(--bg-card); border: 1px solid var(--border-subtle); border-radius: 20px; overflow: hidden; box-shadow: 0 30px 70px -30px rgba(0, 0, 0, 0.6); }

.demo-tabs { display: flex; gap: 4px; padding: 6px; background: rgba(8, 22, 25, 0.5); border-bottom: 1px solid var(--border-subtle); }
.demo-tab {
  flex: 1;
  display: flex; align-items: center; justify-content: center; gap: 7px;
  padding: 13px 8px;
  background: none; border: none; border-radius: 10px;
  font-family: inherit; font-size: 13.5px; font-weight: 700;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s ease;
}
.demo-tab ::v-deep .v-icon { color: currentColor; }
.demo-tab:hover { color: var(--apricot-light); background: rgba(244, 162, 89, 0.07); }
.demo-tab.active { color: var(--apricot-light); background: rgba(244, 162, 89, 0.14); }
.demo-tab:focus-visible { outline: 2px solid var(--apricot); outline-offset: -2px; }

.demo-panel { padding: 32px; }
.demo-visual {
  height: 150px; border-radius: 14px; margin-bottom: 24px;
  display: flex; align-items: center; justify-content: center;
  background:
    radial-gradient(circle at 50% 50%, color-mix(in srgb, var(--tone) 22%, transparent) 0%, transparent 62%),
    repeating-linear-gradient(0deg, transparent 0 29px, rgba(247, 240, 230, 0.04) 29px 30px),
    repeating-linear-gradient(90deg, transparent 0 29px, rgba(247, 240, 230, 0.04) 29px 30px),
    #0D2226;
  border: 1px solid var(--border-subtle);
}
.demo-panel-title { font-size: 1.45rem; font-weight: 600; color: var(--text-primary); margin-bottom: 8px; }
.demo-panel-text { font-size: 0.98rem; line-height: 1.65; color: var(--text-secondary); margin-bottom: 20px; }
.demo-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
.demo-list li { display: flex; align-items: center; gap: 12px; padding: 11px 14px; border-radius: 10px; background: rgba(8, 22, 25, 0.45); border: 1px solid var(--border-subtle); font-size: 0.93rem; color: var(--text-secondary); }
.demo-list__num { width: 24px; height: 24px; flex: 0 0 auto; border-radius: 50%; background: rgba(244, 162, 89, 0.14); border: 1px solid var(--border-apricot); color: var(--apricot-light); font-size: 12px; font-weight: 800; display: flex; align-items: center; justify-content: center; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.fade-enter, .fade-leave-to { opacity: 0; transform: translateY(6px); }

.info-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.stat-item { display: flex; align-items: center; gap: 14px; padding: 16px; border-radius: 14px; background: var(--bg-card); border: 1px solid var(--border-subtle); }
.stat-icon { width: 42px; height: 42px; flex: 0 0 auto; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
.stat-value { font-size: 1.05rem; font-weight: 800; color: var(--text-primary); line-height: 1.2; }
.stat-label { font-size: 0.84rem; color: var(--text-muted); }

/* ==================== WHO IT'S FOR ==================== */
.benefit-card { height: 100%; padding: 32px 28px; border-radius: 18px; background: var(--bg-card); border: 1px solid var(--border-subtle); }
.benefit-badge { font-size: 11.5px; font-weight: 800; letter-spacing: 1.2px; color: var(--apricot-light); margin-bottom: 8px; }
.benefit-title { font-size: 1.5rem; font-weight: 600; color: var(--text-primary); margin-bottom: 10px; }
.benefit-text { font-size: 0.96rem; line-height: 1.65; color: var(--text-secondary); margin-bottom: 18px; }
.benefit-points { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 9px; }
.benefit-points li { position: relative; padding-left: 18px; font-size: 0.92rem; color: var(--text-secondary); }
.benefit-points li::before { content: ''; position: absolute; left: 0; top: 8px; width: 6px; height: 6px; border-radius: 50%; background: var(--apricot); }

/* ==================== PRICING ==================== */
.pricing-section { background: var(--bg-alt); border-top: 1px solid var(--border-subtle); }

.pricing-card {
  display: grid; grid-template-columns: 360px 1fr;
  border-radius: 22px; overflow: hidden;
  background: var(--bg-card);
  border: 1px solid var(--border-apricot);
  box-shadow: 0 40px 90px -40px rgba(0, 0, 0, 0.7);
}
.pricing-summary { padding: 40px 34px; background: radial-gradient(130% 120% at 0% 0%, #174A50 0%, #11292E 65%); border-right: 1px solid var(--border-subtle); }
.pricing-logo { display: flex; width: 56px; height: 56px; border-radius: 50%; background: #F7F0E6; overflow: hidden; box-shadow: 0 0 0 4px rgba(244, 162, 89, 0.18); margin-bottom: 18px; }
.pricing-logo img { width: 100%; height: 100%; object-fit: cover; }
.pricing-plan-name { font-size: 1.6rem; font-weight: 600; color: var(--text-primary); margin-bottom: 4px; }
.pricing-tagline { font-size: 0.95rem; color: var(--text-secondary); margin-bottom: 26px; }
.pricing-amount { display: flex; align-items: baseline; gap: 2px; font-variant-numeric: tabular-nums; }
.pricing-amount .currency { font-size: 1.6rem; font-weight: 700; color: var(--apricot); align-self: flex-start; margin-top: 10px; }
.pricing-amount .amount { font-family: 'Fraunces', Georgia, serif; font-size: 4.6rem; font-weight: 600; line-height: 1; color: var(--text-primary); }
.pricing-amount .period { font-size: 1.05rem; font-weight: 600; color: var(--text-muted); margin-left: 6px; }
.pricing-note { font-size: 0.86rem; color: var(--text-muted); margin: 10px 0 26px; }
.pricing-guarantees { display: flex; flex-direction: column; gap: 10px; }
.pricing-guarantees div { display: flex; align-items: center; gap: 9px; font-size: 0.92rem; color: var(--text-secondary); }

.pricing-features { padding: 40px 36px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px 28px; align-content: center; }
.group-header { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid var(--border-subtle); }
.group-header h4 { font-size: 0.95rem; font-weight: 800; color: var(--text-primary); margin: 0; }
.feature-group ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
.feature-group li { display: flex; align-items: flex-start; gap: 8px; font-size: 0.89rem; line-height: 1.4; color: var(--text-secondary); }
.feature-group li .v-icon { margin-top: 2px; }

/* ==================== FINAL CTA ==================== */
.final-cta-section { position: relative; overflow: hidden; padding: 110px 0; background: var(--bg-deep); border-top: 1px solid var(--border-subtle); }
.cta-orb { position: absolute; width: 640px; height: 640px; left: 50%; top: -360px; transform: translateX(-50%); border-radius: 50%; background: radial-gradient(circle, rgba(244, 162, 89, 0.35) 0%, transparent 70%); filter: blur(90px); pointer-events: none; }
.cta-inner { position: relative; }
.cta-title { font-size: clamp(2rem, 4.2vw, 3rem); font-weight: 600; line-height: 1.15; color: var(--text-primary); max-width: 720px; margin-left: auto; margin-right: auto; }
.cta-subtitle { font-size: 1.1rem; color: var(--text-secondary); max-width: 560px; margin-left: auto; margin-right: auto; }

/* ==================== DEMO DIALOG ==================== */
.demo-card {
  /* Rendered outside .pg-home by v-dialog, so it needs its own tokens */
  --apricot: #F4A259;
  --text-primary: #F7F0E6;
  --text-secondary: rgba(247, 240, 230, 0.72);
}
.demo-card__head h2 { font-family: 'Fraunces', Georgia, serif; letter-spacing: -0.01em; }
.demo-card { background: #11292E; border: 1px solid rgba(247, 240, 230, 0.1); border-radius: 20px; padding: 26px; font-family: 'Figtree', sans-serif; }
.demo-card__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.demo-card__eyebrow { display: block; color: var(--apricot); font-size: 13px; font-weight: 700; margin-bottom: 4px; }
.demo-card__head h2 { font-size: 1.55rem; font-weight: 600; line-height: 1.2; color: var(--text-primary); margin: 0; }
.demo-card__close { width: 36px; height: 36px; flex: 0 0 auto; border-radius: 10px; border: 1px solid rgba(247, 240, 230, 0.1); background: #143036; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.demo-card__close:focus-visible { outline: 2px solid var(--apricot); outline-offset: 2px; }
.demo-card__sub { color: var(--text-secondary); font-size: 0.95rem; line-height: 1.6; margin: 10px 0 20px; }
.demo-form ::v-deep .v-input__slot { background: #173338 !important; border-radius: 10px !important; }
.demo-form ::v-deep fieldset { border-color: rgba(247, 240, 230, 0.12); }
.demo-msg { display: flex; align-items: center; gap: 8px; font-size: 0.9rem; margin: 0 0 12px; }
.demo-msg--error { color: #FF9A7A; }
.demo-msg--ok { color: #6BD6C4; }
.demo-card__actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 4px; }

/* ==================== RESPONSIVE ==================== */
@media (max-width: 1100px) {
  .pricing-card { grid-template-columns: 1fr; }
  .pricing-summary { border-right: none; border-bottom: 1px solid var(--border-subtle); }
}

@media (max-width: 960px) {
  .feature-grid { grid-template-columns: 1fr 1fr; }
  .hero-section { padding: 48px 0 72px; min-height: 0; }
  .hero-subtitle { margin-left: auto; margin-right: auto; }
  .trust-indicators { justify-content: center; }
  .phone-wrap { margin-top: 40px; }
  .features-section, .benefits-section, .pricing-section, .demo-section { padding: 72px 0; }
}

@media (max-width: 700px) {
  .pricing-features { grid-template-columns: 1fr 1fr; padding: 30px 24px; }
}

@media (max-width: 600px) {
  .feature-grid { grid-template-columns: 1fr; }
  .cta-btn { width: 100%; }
  .demo-tab span { display: none; }
  .demo-panel { padding: 22px; }
  .info-stats { grid-template-columns: 1fr; }
  .pricing-features { grid-template-columns: 1fr; }
  .pricing-summary { padding: 30px 24px; }
  .phone-frame { width: 288px; height: 590px; }
  .section-header { margin-bottom: 40px; }
}

@media (prefers-reduced-motion: reduce) {
  .reveal, .hero-eyebrow, .hero-title, .hero-subtitle, .cta-buttons, .trust-indicators, .phone-wrap {
    opacity: 1 !important; transform: none !important; transition: none !important; animation: none !important;
  }
}

/* ---------- Tribzz ---------- */
.tribzz-section { padding: 40px 0 104px; background: var(--bg-page); }
.tribzz-band {
  display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 56px; align-items: center;
  background: linear-gradient(135deg, var(--bg-card) 0%, var(--bg-alt) 100%);
  border: 1px solid var(--border-subtle); border-radius: 28px; padding: 56px;
}
.tribzz-title { text-align: left; }
.tribzz-text { font-size: 1.08rem; line-height: 1.7; color: var(--text-secondary); max-width: 560px; }
.tribzz-points { list-style: none; padding: 0; margin: 22px 0 28px; display: grid; gap: 10px; }
.tribzz-points li { display: flex; gap: 10px; align-items: center; color: var(--text-primary); }
.tribzz-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 22px; }
.tribzz-link { color: var(--apricot-light); font-weight: 600; text-decoration: none; display: inline-flex; align-items: center; gap: 6px; }
.tribzz-link:hover { color: var(--apricot); }
/* Drawn in Tribzz's own look so groomers recognise it there */
.tribzz-mock { display: flex; justify-content: center; }
.tm-card { width: 100%; max-width: 340px; background: #F4EEE6; color: #1A1715; border: 1px solid #DCD2C4; box-shadow: 0 30px 70px rgba(0, 0, 0, 0.4); transform: rotate(1.5deg); }
.tm-cover { height: 120px; background: linear-gradient(135deg, #1A1715, #3A2A22); border-bottom: 4px solid #B8442F; display: flex; align-items: center; justify-content: center; }
.tm-cover span { font-family: 'Fraunces', Georgia, serif; font-size: 54px; font-weight: 700; color: #F4EEE6; }
.tm-body { padding: 16px 18px 18px; }
.tm-title { font-family: 'Fraunces', Georgia, serif; font-size: 21px; font-weight: 700; }
.tm-sub { font-family: Georgia, serif; color: #4A423C; font-size: 14px; margin-top: 2px; }
.tm-chips { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 12px; }
.tm-chips span { border: 1px solid #1A1715; font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; padding: 3px 7px; font-family: ui-monospace, Menlo, monospace; }
.tm-post { margin-top: 12px; padding-left: 10px; border-left: 2px solid #B8442F; font-family: Georgia, serif; font-style: italic; font-size: 14px; }
.tm-join { margin-top: 14px; background: #1A1715; color: #F4EEE6; text-align: center; padding: 9px; letter-spacing: 0.2em; font-size: 11px; font-weight: 700; }
@media (max-width: 960px) {
  .tribzz-band { grid-template-columns: 1fr; padding: 36px 24px; gap: 36px; }
  .tm-card { transform: none; }
}
</style>

<!-- v-dialog renders outside this component, so its wrapper can't be scoped -->
<style>
.pg-demo-dialog { border-radius: 20px !important; box-shadow: 0 40px 90px rgba(0, 0, 0, 0.6) !important; }
.pg-demo-dialog, .pg-demo-dialog .v-btn, .pg-demo-dialog .v-input { font-family: 'Figtree', -apple-system, BlinkMacSystemFont, sans-serif !important; }
.pg-demo-dialog .v-btn { text-transform: none !important; letter-spacing: 0 !important; }
.pg-demo-dialog .btn-apricot.v-btn--disabled { color: rgba(42, 22, 8, 0.6) !important; }
</style>